import React, { useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  ReactRequiredChildren,
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";
import { useGroup } from "./GroupContext";
import { useRow } from "./RowContext";

import styles from "./ComparisonTable.scss";

const variantValues = ["primary", "secondary"] as const;
type VariantValue = typeof variantValues[number];

type ComparisonTableColHeaderBaseProps = {
  id?: string;
  variant?: VariantValue;
};

export type ComparisonTableColHeaderProps = ComparisonTableColHeaderBaseProps &
  Omit<
    ReactRequiredChildren<React.ComponentPropsWithoutRef<"th">>,
    keyof ComparisonTableColHeaderBaseProps
  >;

const defaultProps = {
  id: "",
  variant: "primary",
} as const;

const validatedDefaultProps: ReactDefaultProps<ComparisonTableColHeaderBaseProps> = defaultProps;

export const ComparisonTableColHeader: ((
  props: ComparisonTableColHeaderProps
) => JSX.Element) &
  ReactComponentStaticProps<ComparisonTableColHeaderProps> = ({
  children,
  id = defaultProps.id,
  variant = defaultProps.variant,
  ...restProps
}) => {
  const { headerRowId } = useGroup();
  const { setHeaderColId } = useRow();
  const variantStyleName = `col-header--${variant}` as const;

  useEffect(() => {
    setHeaderColId(id);
  }, [id]);

  return (
    <th
      scope="row"
      id={id}
      className={cx(
        styles["col-header"],
        styles.fixed,
        styles[variantStyleName]
      )}
      headers={headerRowId}
      {...restProps}
    >
      <div className={styles["sticky-background-cover"]}>{children}</div>
    </th>
  );
};

ComparisonTableColHeader.propTypes = {
  id: PropTypes.string,
  variant: PropTypes.oneOf(variantValues),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

ComparisonTableColHeader.defaultProps = validatedDefaultProps;

ComparisonTableColHeader.displayName = "ComparisonTable.ColHeader";

export default ComparisonTableColHeader;
